import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, setAuthToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Sparkles, Lock, Building2, Mail, ShieldAlert } from "lucide-react";
import { useT } from "@/lib/i18n";
import LanguageSelector from "@/components/LanguageSelector";

export default function Register() {
  const nav = useNavigate();
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  // Host-aware configuration. Tells us whether /register is allowed on
  // this domain (savvaspos.store blocks it) + the live trial / price.
  const [hostCfg, setHostCfg] = useState({
    register_enabled: true,
    trial_days: 60,
    price_eur: 8.0,
    mode: "saas",
  });

  useEffect(() => {
    api.get("/host-config")
      .then((r) => setHostCfg(r.data || {}))
      .catch(() => { /* fall back to optimistic defaults */ });
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password) {
      toast.error("Email & password required");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        email: email.trim().toLowerCase(),
        password,
        company_name: company.trim() || null,
      });
      setAuthToken(data.token);
      // Iter 54 — Meta Pixel CompleteRegistration conversion event.
      // The fbq() stub is always defined by /public/index.html so this
      // line is a safe no-op until a real Pixel ID is configured.
      try {
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          window.fbq("track", "CompleteRegistration", {
            value: 8.00,
            currency: "EUR",
            content_name: "kioskforce_trial_signup",
          });
        }
      } catch { /* tolerate any pixel failure silently */ }
      toast.success(`Welcome aboard — ${data.tenant.trial_days_left}-day free trial started`);
      nav("/onboarding", { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center p-6" data-testid="register-page">
      <div className="absolute top-5 right-5 z-10">
        <LanguageSelector variant="light" />
      </div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-900 mb-4">
            <Sparkles className="h-7 w-7" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">KioskForce ERP</h1>
          <p className="text-slate-400 mt-2 text-sm">{t("register.subtitle")}</p>
        </div>

        <Card className="p-6 bg-white text-slate-900">
          {!hostCfg.register_enabled && (
            <div
              className="mb-4 p-3 rounded-lg border-2 border-amber-400 bg-amber-50 text-amber-900 flex gap-2 items-start"
              data-testid="register-blocked-banner"
            >
              <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="text-[12px] leading-tight">
                <div className="font-bold">
                  Self-registration disabled on this domain.
                </div>
                <div className="mt-1">
                  Visit{" "}
                  <a href={`${process.env.REACT_APP_SAAS_HOST}/register`} className="underline font-bold">
                    {(process.env.REACT_APP_SAAS_HOST || "").replace(/^https?:\/\//, "")}
                  </a>{" "}
                  to start your free trial — or{" "}
                  <Link to="/saas-login" className="underline font-bold">
                    sign in here
                  </Link>{" "}
                  if you already have an account.
                </div>
              </div>
            </div>
          )}

          <h2 className="text-xl font-bold mb-1">{t("register.title")}</h2>
          <p className="text-xs text-slate-500 mb-2" data-testid="register-trial-line">
            <span className="font-bold text-emerald-700">
              {hostCfg.trial_days || 60} Μήνες Εντελώς ΔΩΡΕΑΝ Δοκιμή · {hostCfg.trial_days || 60}-Day Free Trial
            </span>
          </p>
          <p className="text-xs text-slate-500 mb-5" data-testid="register-price-line">
            €{Number(hostCfg.price_eur || 8).toFixed(0)} / month after your {Math.round((hostCfg.trial_days || 60) / 30)}-month trial · Cancel anytime
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Mail className="h-3 w-3" /> {t("register.email")}
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbusiness.com"
                required
                data-testid="register-email"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Lock className="h-3 w-3" /> {t("register.password")}
              </Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                required
                minLength={6}
                data-testid="register-password"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Building2 className="h-3 w-3" /> {t("register.companyName")}
              </Label>
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g. My Kiosks Ltd"
                data-testid="register-company"
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !hostCfg.register_enabled}
              className="w-full h-11 mt-2 bg-slate-900 hover:bg-slate-800 text-white font-bold disabled:bg-slate-400 disabled:cursor-not-allowed"
              data-testid="register-submit"
            >
              {busy ? "…" : !hostCfg.register_enabled ? "Disabled on this domain" : t("register.create")}
            </Button>
          </form>

          <p className="text-xs text-slate-500 mt-5 text-center">
            {t("register.alreadyHave")}{" "}
            <Link to="/saas-login" className="font-bold text-slate-900 underline" data-testid="link-to-login">
              {t("register.signIn")}
            </Link>
          </p>
        </Card>

        <p className="text-[11px] text-slate-500 mt-6 text-center">
          By signing up you agree to our terms · Cyprus VAT-compliant · GDPR
        </p>
      </div>
    </div>
  );
}
